import { useState } from 'react';
import { useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import {
  getGetMeQueryKey,
  getListOrganizationMembersQueryKey,
  getListOrganizationMembersQueryOptions,
  useGetMe,
  useRemoveOrganizationMember,
  useUpdateOrganizationMember,
  useSetUserSuperAdmin,
  type OrganizationMemberDto,
} from '@sara/api-client';
import { toSuspense } from '../../../../lib/query';
import { getApiErrorMessage } from '../../../../lib/api-error';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@sara/ui/components/alert-dialog';
import { buttonVariants } from '@sara/ui/components/button';
import { toast } from '@sara/ui/components/sonner';
import { cn } from '@sara/ui/lib/utils';
import { useOrgSlug } from '../../../auth';
import { TeamMemberList, type MemberRole } from '../../components/TeamMemberList';
import { PendingInviteList } from '../../components/PendingInviteList';
import type { TeamListContainerProps } from './types';

export function TeamListContainerContent({ orgId }: TeamListContainerProps) {
  const queryClient = useQueryClient();
  const orgSlug = useOrgSlug();
  const [memberToRemove, setMemberToRemove] = useState<OrganizationMemberDto | null>(null);

  const { data: rows } = useSuspenseQuery(
    toSuspense(getListOrganizationMembersQueryOptions({ orgId }))
  );
  const { data: me } = useGetMe();

  const membership = me?.memberships.find((m) => m.organization.id === orgId);
  const canManage = membership?.role === 'ADMIN';
  const currentUserId = me?.id ?? null;

  const members = rows.filter((row) => row.status === 'ACTIVE');
  const pendingInvites = rows.filter((row) => row.status === 'PENDING');

  function invalidateMembers() {
    void queryClient.invalidateQueries({ queryKey: getListOrganizationMembersQueryKey({ orgId }) });
    // Role changes / removals can target the caller themselves — refresh /me
    // so canManage (and org access) reflect the new state without a reload.
    void queryClient.invalidateQueries({ queryKey: getGetMeQueryKey() });
  }

  const {
    mutate: updateMember,
    isPending: isUpdatingRole,
    variables: updateVariables,
  } = useUpdateOrganizationMember({
    mutation: {
      onSuccess: (data) => {
        invalidateMembers();
        toast.success(`${data.email} is now ${data.role === 'ADMIN' ? 'an admin' : 'a member'}`);
      },
      onError: (error) => {
        toast.error('Failed to change role', { description: getApiErrorMessage(error) });
      },
    },
  });

  const {
    mutate: setSuperAdmin,
    isPending: isSettingSuperAdmin,
    variables: superAdminVariables,
  } = useSetUserSuperAdmin({
    mutation: {
      onSuccess: (data) => {
        invalidateMembers();
        toast.success(
          data.isSuperAdmin ? 'Granted super admin access' : 'Revoked super admin access'
        );
      },
      onError: (error) => {
        toast.error('Failed to change super admin', { description: getApiErrorMessage(error) });
      },
    },
  });

  const { mutate: removeMember, isPending: isRemoving } = useRemoveOrganizationMember({
    mutation: {
      onSuccess: (_data, variables) => {
        const removed = rows.find((row) => row.id === variables.id);
        invalidateMembers();
        setMemberToRemove(null);
        toast.success(
          removed?.status === 'PENDING'
            ? `Revoked the invite for ${removed.email}`
            : `Removed ${removed?.email ?? 'member'} from the organization`
        );
      },
      onError: (error) => {
        toast.error('Failed to remove', { description: getApiErrorMessage(error) });
      },
    },
  });

  function copyInviteLink() {
    const link = `${window.location.origin}/orgs/${orgSlug}`;
    navigator.clipboard
      .writeText(link)
      .then(() =>
        toast.success('Sign-in link copied', {
          description: 'Invitees get access automatically once they sign in with their email.',
        })
      )
      .catch(() => toast.error('Could not copy the link'));
  }

  const isRemovingInvite = memberToRemove?.status === 'PENDING';

  return (
    <div className="space-y-10">
      <TeamMemberList
        members={members}
        currentUserId={currentUserId}
        canManage={canManage}
        onChangeRole={(member, role: MemberRole) =>
          updateMember({ id: member.id, params: { orgId }, data: { role } })
        }
        changingRoleId={isUpdatingRole ? (updateVariables?.id ?? null) : null}
        onRemove={setMemberToRemove}
        canManageSuperAdmin={Boolean(me?.isSuperAdmin)}
        onToggleSuperAdmin={(member, isSuperAdmin) => {
          if (member.userId) setSuperAdmin({ id: member.userId, data: { isSuperAdmin } });
        }}
        togglingSuperAdminUserId={isSettingSuperAdmin ? (superAdminVariables?.id ?? null) : null}
      />

      {pendingInvites.length > 0 && (
        <section className="space-y-3">
          <h2 className="font-display text-lg font-medium tracking-tight text-foreground">
            Pending invites
          </h2>
          <PendingInviteList
            invites={pendingInvites}
            canManage={canManage}
            onCopyLink={copyInviteLink}
            onRevoke={setMemberToRemove}
          />
        </section>
      )}

      <AlertDialog
        open={memberToRemove !== null}
        onOpenChange={(open) => {
          if (!open && !isRemoving) setMemberToRemove(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {isRemovingInvite ? 'Revoke invite?' : 'Remove member?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {memberToRemove ? (
                isRemovingInvite ? (
                  <>
                    <span className="font-medium text-foreground">{memberToRemove.email}</span> will
                    no longer get access when they sign in. You can invite them again later.
                  </>
                ) : (
                  <>
                    <span className="font-medium text-foreground">{memberToRemove.email}</span>{' '}
                    immediately loses access to this organization. Their account and interview data
                    are kept.
                  </>
                )
              ) : null}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              className={cn(buttonVariants({ variant: 'outline', shape: 'pill', size: 'sm' }))}
              disabled={isRemoving}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              className={cn(buttonVariants({ variant: 'destructive', shape: 'pill', size: 'sm' }))}
              disabled={isRemoving}
              data-testid="confirm-remove"
              onClick={(event) => {
                event.preventDefault();
                if (!memberToRemove) return;
                removeMember({ id: memberToRemove.id, params: { orgId } });
              }}
            >
              {isRemoving ? 'Removing…' : isRemovingInvite ? 'Revoke invite' : 'Remove member'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
